// packages/client/src/modules/admin/components/ProdutoConfigTab.tsx
// AIDEV-NOTE: Aba de configurações do dialog de produto — toggle de visibilidade + grade.
import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useProdutoConfig } from '@/hooks/useProdutoConfig';

interface Props {
  produtoCodigo: number;
}

export function ProdutoConfigTab({ produtoCodigo }: Props) {
  const {
    config,
    isLoading,
    patchProduto,
    patchVariante,
    isPatchingProduto,
    isPatchingVariante,
    isErrorProduto,
    isErrorVariante
  } = useProdutoConfig(produtoCodigo);

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        <Skeleton height={72} sx={{ borderRadius: 1 }} />
        <Skeleton height={160} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  if (!config) {
    return <Alert severity="error">Erro ao carregar configurações do produto.</Alert>;
  }

  const temGrade = config.variantes.length > 0;

  return (
    <Box>
      {(isErrorProduto || isErrorVariante) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Falha ao salvar a alteração — verifique a conexão e tente novamente.
        </Alert>
      )}

      {/* Toggle do produto inteiro */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: config.ativo_venda ? 'rgba(46, 125, 50, 0.08)' : 'grey.100',
          borderRadius: 1,
          mb: 3
        }}
      >
        <Box>
          <Typography variant="subtitle2">Ativo para vendas</Typography>
          <Typography variant="caption" color="text.secondary">
            {config.ativo_venda
              ? 'Produto aparece no catálogo dos vendedores'
              : 'Produto oculto para vendedores'}
          </Typography>
        </Box>
        <Switch
          checked={config.ativo_venda}
          disabled={isPatchingProduto}
          onChange={(e) => patchProduto(e.target.checked)}
        />
      </Box>

      {/* Tabela de grade (só para produtos com variantes) */}
      {temGrade && (
        <Box sx={{ opacity: config.ativo_venda ? 1 : 0.5, transition: 'opacity 0.2s' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Grade (cor × tamanho)
          </Typography>

          {!config.ativo_venda && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Produto inativo — reative o produto para gerenciar variantes individualmente.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cor</TableCell>
                  <TableCell>Tamanho</TableCell>
                  <TableCell align="center">Ativo p/ vendas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.variantes.map((v) => (
                  <TableRow key={`${v.codcor}-${v.codtamanho}`}>
                    <TableCell>{v.cor ?? `Cor ${v.codcor}`}</TableCell>
                    <TableCell>{v.tamanho ?? `Tam ${v.codtamanho}`}</TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={!config.ativo_venda ? 'Reative o produto primeiro' : ''}
                        disableHoverListener={config.ativo_venda}
                      >
                        <span>
                          <Switch
                            size="small"
                            checked={v.ativo_venda}
                            disabled={!config.ativo_venda || isPatchingVariante}
                            onChange={(e) =>
                              patchVariante({
                                codcor: v.codcor,
                                codtamanho: v.codtamanho,
                                ativo_venda: e.target.checked
                              })
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
